'use client'

import { useId } from 'react'
import {
  Briefcase,
  Building2,
  Calculator,
  ShoppingBag,
  Stethoscope,
  ArrowRight,
  TrendingUp,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { usePricing } from '@/context/pricing-context'

// --- Minimal Slider Component ---
function MinimalSlider({
  value,
  min,
  max,
  onChange,
  step = 1,
  label,
  suffix = '',
}: {
  value: number
  min: number
  max: number
  onChange: (val: number) => void
  step?: number
  label: string
  suffix?: string
}) {
  const inputId = useId()
  const percentage = ((value - min) / (max - min)) * 100

  return (
    <div className="group space-y-4">
      <div className="flex items-center justify-between">
        <label htmlFor={inputId} className="text-foreground text-sm font-medium">
          {label}
        </label>
        <span className="text-foreground font-mono text-sm font-semibold">
          {value.toLocaleString()}
          {suffix}
        </span>
      </div>
      <div className="relative flex h-6 items-center">
        <input
          id={inputId}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="peer absolute z-20 h-full w-full cursor-pointer opacity-0"
        />
        {/* Track Background */}
        <div className="bg-secondary relative z-10 h-1.5 w-full overflow-hidden rounded-full">
          {/* Active Track */}
          <div
            className="bg-primary h-full transition-[width] duration-150 motion-reduce:transition-none"
            style={{ width: `${percentage}%` }}
          />
        </div>
        {/* Thumb */}
        <div
          className="border-primary/20 bg-background ring-primary/10 peer-focus:ring-ring pointer-events-none absolute z-10 h-4 w-4 rounded-full border shadow-sm ring-1 transition-[transform,box-shadow,border-color,background-color] duration-150 group-hover:scale-110 peer-focus:ring-2 motion-reduce:transition-none"
          style={{ left: `calc(${percentage}% - 8px)` }}
        />
      </div>
    </div>
  )
}

const presets = [
  { id: 'dental', label: 'Clínica', icon: Stethoscope, conv: 3000, time: 8, rate: 12 },
  { id: 'realestate', label: 'Inmobiliaria', icon: Building2, conv: 5000, time: 15, rate: 18 },
  { id: 'ecommerce', label: 'E-commerce', icon: ShoppingBag, conv: 10000, time: 5, rate: 10 },
  { id: 'saas', label: 'SaaS B2B', icon: Briefcase, conv: 1500, time: 20, rate: 25 },
]

export function RoiCalculatorSection() {
  const {
    conversations,
    setConversations,
    timePerConv,
    setTimePerConv,
    hourlyRate,
    setHourlyRate,
    monthlySavings,
    yearlySavings,
    hoursSaved,
    recommendedPlan,
    scrollToPricing,
  } = usePricing()

  const applyPreset = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId)
    if (preset) {
      setConversations(preset.conv)
      setTimePerConv(preset.time)
      setHourlyRate(preset.rate)
    }
  }

  return (
    <section className="border-border bg-muted/30 relative overflow-hidden border-y py-24 md:py-32">
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-16 max-w-2xl text-center">
          <Badge variant="outline" className="bg-background mb-4 px-3 py-1">
            <Calculator className="mr-2 h-3 w-3" aria-hidden="true" />
            Simulador de Ahorro
          </Badge>
          <h2 className="text-foreground text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            Calcula cuánto estás perdiendo hoy
          </h2>
          <p className="text-muted-foreground mt-4 text-lg">
            Descubre el impacto real de automatizar tu atención al cliente.
          </p>
        </div>

        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-12">
          {/* Controls Panel */}
          <Card className="border-border bg-background shadow-sm lg:col-span-7">
            <CardContent className="p-8">
              <div>
                <label className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
                  Selecciona tu industria
                </label>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {presets.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyPreset(preset.id)}
                      className="border-border bg-secondary/50 hover:border-primary/20 hover:bg-secondary group flex flex-col items-center justify-center gap-3 rounded-xl border p-4 transition-[transform,box-shadow,border-color,background-color,color] active:scale-95 motion-reduce:transition-none"
                    >
                      <preset.icon
                        className="text-muted-foreground group-hover:text-foreground h-5 w-5 transition-colors"
                        aria-hidden="true"
                      />
                      <span className="text-muted-foreground group-hover:text-foreground text-xs font-medium">
                        {preset.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-border my-8 h-px w-full" />

              <div className="space-y-8">
                <MinimalSlider
                  label="Conversaciones al mes"
                  value={conversations}
                  min={100}
                  max={15000}
                  step={100}
                  onChange={setConversations}
                />
                <MinimalSlider
                  label="Minutos promedio por chat"
                  value={timePerConv}
                  min={1}
                  max={60}
                  suffix=" min"
                  onChange={setTimePerConv}
                />
                <MinimalSlider
                  label="Costo de agente por hora"
                  value={hourlyRate}
                  min={5}
                  max={100}
                  suffix=" USD"
                  onChange={setHourlyRate}
                />
              </div>
            </CardContent>
          </Card>

          {/* Results Panel */}
          <Card className="bg-foreground text-background flex flex-col justify-between border-none shadow-2xl lg:col-span-5">
            <CardContent className="flex h-full flex-col justify-between p-8">
              <div className="space-y-6">
                <div>
                  <p className="text-background/60 text-sm font-medium">Dinero recuperado al año</p>
                  <div className="mt-2 text-5xl font-bold tracking-tighter sm:text-6xl">
                    ${Math.round(yearlySavings).toLocaleString()}
                  </div>
                </div>

                <div className="border-background/10 grid grid-cols-2 gap-4 border-t pt-6">
                  <div>
                    <div className="text-background/60 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" aria-hidden="true" />
                      <span className="text-xs font-medium uppercase tracking-wider">
                        Ahorro Mensual
                      </span>
                    </div>
                    <p className="mt-1 text-2xl font-bold">
                      ${Math.round(monthlySavings).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <div className="text-background/60 flex items-center gap-2">
                      <Calculator className="h-4 w-4" aria-hidden="true" />
                      <span className="text-xs font-medium uppercase tracking-wider">
                        Horas Libres
                      </span>
                    </div>
                    <p className="mt-1 text-2xl font-bold">
                      {Math.round(hoursSaved).toLocaleString()}h
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 space-y-4 pt-8">
                <div className="bg-background/10 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-background/80 text-xs font-medium">
                    Plan recomendado para tu volumen:
                  </p>
                  <div className="mt-1 flex items-baseline justify-between">
                    <span className="text-xl font-bold capitalize">{recommendedPlan}</span>
                    <span className="text-sm opacity-60">
                      {conversations.toLocaleString()} chats/mes
                    </span>
                  </div>
                </div>

                <Button
                  onClick={scrollToPricing}
                  className="bg-background text-foreground hover:bg-background/90 group h-12 w-full justify-between rounded-full px-6"
                >
                  <span className="font-semibold">Ver precios del plan</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
